import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { MapPin, Calendar, Users, Clock, Shirt, Star, ArrowLeft, Loader2 } from 'lucide-react';
import ReviewModal from '../components/ReviewModal';

const WeddingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, isAuthenticated, isGuest, isHost, user, token } = useAuth();
  const { t, formatCurrency, formatDate } = useLanguage();

  const [wedding, setWedding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [hasPaidApplication, setHasPaidApplication] = useState(false);
  const [applicationMessage, setApplicationMessage] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewedUser, setReviewedUser] = useState(null);

  useEffect(() => {
    const fetchWedding = async () => {
      try {
        const response = await api.get(`/weddings/${id}`);
        setWedding(response.data);
      } catch (error) {
        console.error('Failed to fetch wedding:', error);
        toast.error('Wedding not found');
        navigate('/weddings');
      } finally {
        setLoading(false);
      }
    };

    const checkApplication = async () => {
      if (isAuthenticated && isGuest) {
        try {
          const response = await api.get('/applications/guest/me');
          const applied = response.data.some((app) => app.wedding_id === id);
          const paid = response.data.some(
            (app) => app.wedding_id === id && app.payment_status === 'paid'
          );
          setHasApplied(applied);
          setHasPaidApplication(paid);
        } catch (error) {
          console.error('Failed to check application:', error);
        }
      }
    };

    fetchWedding();
    checkApplication();
  }, [id, isAuthenticated, isGuest, api, navigate]);

  const handleApply = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!isGuest) {
      toast.error('Only guests can apply to weddings');
      return;
    }

    setApplying(true);
    try {
      await api.post('/applications', {
        wedding_id: id,
        message: applicationMessage,
      });
      toast.success('Application submitted successfully!');
      setHasApplied(true);
      setDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setApplying(false);
    }
  };

  const defaultImage = "https://images.pexels.com/photos/33485955/pexels-photo-33485955.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-secondary py-8">
          <div className="max-w-5xl mx-auto px-4">
            <Skeleton className="h-[400px] rounded-2xl mb-8" />
            <Skeleton className="h-8 w-2/3 mb-4" />
            <Skeleton className="h-4 w-1/2 mb-8" />
            <div className="grid grid-cols-2 gap-4">
              <Skeleton className="h-24" />
              <Skeleton className="h-24" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!wedding) return null;

  return (
    <Layout>
      <div className="min-h-screen bg-secondary" data-testid="wedding-detail-page">
        {/* Hero Image */}
        <div className="relative h-[50vh] min-h-[400px]">
          <img
            src={wedding.images?.[0] || defaultImage}
            alt={wedding.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          
          <Button
            variant="ghost"
            className="absolute top-6 left-6 text-white hover:bg-white/20"
            onClick={() => navigate(-1)}
            data-testid="back-button"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('common.back')}
          </Button>

          <div className="absolute bottom-0 left-0 right-0 p-8">
            <div className="max-w-5xl mx-auto">
              {wedding.is_featured && (
                <Badge className="bg-primary text-white mb-4">Featured</Badge>
              )}
              <h1 className="font-serif text-3xl md:text-5xl font-bold text-white mb-2">
                {wedding.title}
              </h1>
              <div className="flex items-center text-white/80 space-x-4">
                <span className="flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  {wedding.location}, {wedding.country}
                </span>
                <span className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  {formatDate(wedding.date)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              <Card>
                <CardContent className="p-6">
                  <h2 className="font-serif text-2xl font-semibold mb-4">
                    {t('wedding.detail.about')}
                  </h2>
                  <p className="text-muted-foreground whitespace-pre-line">
                    {wedding.description}
                  </p>
                </CardContent>
              </Card>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="p-4 flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{t('wedding.detail.date')}</p>
                      <p className="font-medium">{formatDate(wedding.date)}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4 flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <Users className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{t('wedding.detail.slots')}</p>
                      <p className="font-medium">{wedding.available_slots} / {wedding.total_slots}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4 flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <MapPin className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{t('wedding.detail.location')}</p>
                      <p className="font-medium">{wedding.location}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4 flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <Shirt className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{t('wedding.detail.dresscode')}</p>
                      <p className="font-medium">{wedding.dresscode || 'Formal'}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card className="sticky top-24">
                <CardContent className="p-6">
                  <div className="text-center mb-6">
                    <p className="text-sm text-muted-foreground mb-1">{t('wedding.detail.price')}</p>
                    <p className="text-4xl font-bold text-primary">
                      {formatCurrency(wedding.price_per_guest, wedding.currency)}
                    </p>
                    <p className="text-sm text-muted-foreground">{t('wedding.card.perGuest')}</p>
                  </div>

                  {hasApplied ? (
                    <Button
                      className="w-full rounded-full"
                      variant="outline"
                      disabled
                      data-testid="already-applied-btn"
                    >
                      {t('wedding.detail.alreadyApplied')}
                    </Button>
                  ) : wedding.available_slots <= 0 ? (
                    <Button className="w-full rounded-full" disabled>
                      Fully Booked
                    </Button>
                  ) : (
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                      <DialogTrigger asChild>
                        <Button className="w-full rounded-full" data-testid="apply-btn">
                          {t('wedding.detail.apply')}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle className="font-serif">Apply to {wedding.title}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 pt-4">
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Message (optional)</label>
                            <Textarea
                              placeholder="Tell the host a bit about yourself..."
                              value={applicationMessage}
                              onChange={(e) => setApplicationMessage(e.target.value)}
                              rows={4}
                              data-testid="application-message"
                            />
                          </div>
                          <Button
                            className="w-full rounded-full"
                            onClick={handleApply}
                            disabled={applying}
                            data-testid="submit-application-btn"
                          >
                            {applying ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Submitting...
                              </>
                            ) : (
                              'Submit Application'
                            )}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}

                  <p className="text-xs text-muted-foreground text-center mt-4">
                    Payment required after approval
                  </p>
                </CardContent>
              </Card>

              {isAuthenticated && isGuest && wedding.status === 'completed' && hasPaidApplication && (
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Star className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold">{t('review.writeReview')}</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {t('review.completedWedding')}
                        </p>
                        <Button
                          className="w-full rounded-full mt-4"
                          onClick={() => {
                            setReviewedUser({
                              id: wedding.host_id,
                              name: wedding.host_name,
                            });
                            setShowReviewModal(true);
                          }}
                          data-testid="write-review-btn"
                        >
                          {t('review.writeReview')}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Host Info */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-4">{t('wedding.detail.host')}</h3>
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <Star className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{wedding.host_name}</p>
                      <p className="text-sm text-muted-foreground">Wedding Host</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Review Modal */}
        {showReviewModal && reviewedUser && (
          <ReviewModal
            isOpen={showReviewModal}
            onClose={() => {
              setShowReviewModal(false);
              setReviewedUser(null);
            }}
            wedding={wedding}
            reviewedUser={reviewedUser}
            token={token}
            onSuccess={() => {
              setShowReviewModal(false);
              setReviewedUser(null);
            }}
          />
        )}
      </div>
    </Layout>
  );
};

export default WeddingDetail;
